// ============================================
// useStateAndRef — state + always-current ref pair
// ============================================
// Replaces the very common shadow-ref pattern in VoiceBotPanel.tsx:
//
//   const [foo, setFoo] = useState(initial)
//   const fooRef = useRef(initial)
//   useEffect(() => { fooRef.current = foo }, [foo])
//
// Returns the same shape but the setter writes the ref SYNCHRONOUSLY
// (before scheduling the React update), so callbacks that read
// `ref.current` immediately after `setX(...)` see the new value
// without waiting for the next render — matching what the original
// code achieved by also writing `ref.current` imperatively at every
// setter site.
//
// Notes:
//   - The ref identity is stable across renders.
//   - The setter identity is stable across renders.
//   - Functional updates are supported: `setX(prev => prev + 1)` reads
//     `ref.current` (same as React's queue at that moment for this
//     local-only state), then writes the ref + schedules React.
//   - This does NOT replace `useState` for cases where you only ever
//     read the value during render. Only use it where the codebase
//     already maintains a parallel ref for callback access.

import { Dispatch, MutableRefObject, SetStateAction, useCallback, useRef, useState } from 'react'

export const useStateAndRef = <T,>(
  initial: T
): [T, Dispatch<SetStateAction<T>>, MutableRefObject<T>] => {
  const [value, setValueRaw] = useState<T>(initial)
  const ref = useRef<T>(initial)

  const setValue: Dispatch<SetStateAction<T>> = useCallback((next) => {
    const resolved =
      typeof next === 'function'
        ? (next as (p: T) => T)(ref.current)
        : next
    // Write ref synchronously so any callback reading ref.current
    // right after setValue() sees the new value immediately, even
    // before React commits.
    ref.current = resolved
    setValueRaw(resolved)
  }, [])

  return [value, setValue, ref]
}
